import React, { useEffect, useMemo, useState } from 'react';
import './Admin.css';
import {
  GetObjectCommand,
  PutObjectCommand,
  S3Client
} from '@aws-sdk/client-s3';
import { Upload } from '@aws-sdk/lib-storage';

const ADMIN_USER = process.env.REACT_APP_ADMIN_USER || '';
const ADMIN_PASS = process.env.REACT_APP_ADMIN_PASSWORD || '';

const normalizeEventName = (value) => value.trim().replace(/\s+/g, ' ');
const makeFolderKey = (value) =>
  normalizeEventName(value)
    .replace(/[\\]+/g, '-')
    .replace(/\/+/g, '-')
    .trim();

const streamToString = async (body) => {
  if (!body) return '';
  if (typeof body === 'string') return body;
  if (body.text) return body.text();
  try {
    return await new Response(body).text();
  } catch {
    return '';
  }
};

const Admin = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const [eventName, setEventName] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [statusMessage, setStatusMessage] = useState('');
  const [isBusy, setIsBusy] = useState(false);

  const [manifest, setManifest] = useState({});
  const [isLoadingManifest, setIsLoadingManifest] = useState(false);

  const [configError, setConfigError] = useState('');

  const config = useMemo(() => {
    const prefix = (process.env.REACT_APP_S3_PREFIX || 'images/gallery/').replace(/^\/+|\/+$/g, '');
    return {
      bucket: process.env.REACT_APP_S3_BUCKET || '',
      region: process.env.REACT_APP_S3_REGION || '',
      prefix: prefix ? `${prefix}/` : '',
      accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID || '',
      secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY || '',
      makePublic: (process.env.REACT_APP_S3_SET_PUBLIC || '').toLowerCase() === 'true'
    };
  }, []);

  const s3Client = useMemo(() => {
    if (!config.bucket || !config.region || !config.accessKeyId || !config.secretAccessKey) {
      return null;
    }
    return new S3Client({
      region: config.region,
      credentials: {
        accessKeyId: config.accessKeyId,
        secretAccessKey: config.secretAccessKey
      }
    });
  }, [config]);

  useEffect(() => {
    if (!config.bucket || !config.region) {
      setConfigError('Missing S3 bucket or region configuration. Update your environment variables.');
    } else if (!config.accessKeyId || !config.secretAccessKey) {
      setConfigError('Missing AWS access keys. Provide temporary credentials via environment variables.');
    } else if (!ADMIN_USER || !ADMIN_PASS) {
      setConfigError('Missing admin credentials. Configure REACT_APP_ADMIN_USER and REACT_APP_ADMIN_PASSWORD.');
    } else {
      setConfigError('');
    }
  }, [config, s3Client]);

  useEffect(() => {
    if (isAuthenticated) {
      loadManifest();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const loadManifest = async () => {
    if (!s3Client) return;
    setIsLoadingManifest(true);
    try {
      const response = await s3Client.send(
        new GetObjectCommand({
          Bucket: config.bucket,
          Key: `${config.prefix}manifest.json`
        })
      );
      const text = await streamToString(response.Body);
      if (!text) {
        setManifest({});
      } else {
        setManifest(JSON.parse(text));
      }
    } catch (err) {
      if (err?.name === 'NoSuchKey') {
        setManifest({});
      } else {
        console.error(err);
        setStatusMessage('Failed to load manifest. Check S3 permissions.');
      }
    } finally {
      setIsLoadingManifest(false);
    }
  };

  const handleLogin = (event) => {
    event.preventDefault();
    if (configError) {
      setAuthError(configError);
      return;
    }
    if (username === ADMIN_USER && password === ADMIN_PASS) {
      setIsAuthenticated(true);
      setAuthError('');
      setPassword('');
    } else {
      setAuthError('Invalid credentials.');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setUsername('');
    setPassword('');
    setSelectedFiles([]);
    setStatusMessage('');
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files || []);
    setSelectedFiles(files);
  };

  const handleSelectExisting = (event) => {
    const value = event.target.value;
    if (value) {
      setEventName(value);
    }
  };

  const handleUpload = async (event) => {
    event.preventDefault();
    if (!isAuthenticated) return;
    if (!s3Client) {
      setStatusMessage('S3 client not configured.');
      return;
    }
    const title = normalizeEventName(eventName);
    if (!title) {
      setStatusMessage('Event name is required.');
      return;
    }
    if (!selectedFiles.length) {
      setStatusMessage('Select at least one image to upload.');
      return;
    }

    setIsBusy(true);
    setStatusMessage('Uploading images to S3...');

    try {
      const folderKey = makeFolderKey(title);
      const uploadedFileNames = [];

      for (const file of selectedFiles) {
        const key = `${config.prefix}${folderKey}/${file.name}`;
        const uploader = new Upload({
          client: s3Client,
          params: {
            Bucket: config.bucket,
            Key: key,
            Body: file,
            ContentType: file.type || 'application/octet-stream',
            ACL: config.makePublic ? 'public-read' : undefined
          }
        });
        await uploader.done();
        uploadedFileNames.push(file.name);
      }

      const current = manifest[title] || [];
      const nextManifest = {
        ...manifest,
        [title]: Array.from(new Set([...current, ...uploadedFileNames]))
      };

      const manifestBody = JSON.stringify(nextManifest, null, 2);
      await s3Client.send(
        new PutObjectCommand({
          Bucket: config.bucket,
          Key: `${config.prefix}manifest.json`,
          Body: manifestBody,
          ContentType: 'application/json',
          CacheControl: 'no-cache, max-age=0'
        })
      );

      setManifest(nextManifest);
      setSelectedFiles([]);
      setStatusMessage('Upload complete. Manifest updated.');
    } catch (err) {
      console.error(err);
      setStatusMessage(err?.message ? `Error: ${err.message}` : 'Upload failed. Check console for details.');
    } finally {
      setIsBusy(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <main className="admin-page">
        <section className="admin-card">
          <h1>Admin Login</h1>
          <p className="admin-helper">
            Provide the credentials configured via environment variables to manage gallery events.
          </p>
          <form className="admin-form" onSubmit={handleLogin}>
            <label>
              Username
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
              />
            </label>
            <label>
              Password
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
              />
            </label>
            {authError && <div className="admin-error">{authError}</div>}
            {configError && <div className="admin-warning">{configError}</div>}
            <button type="submit" disabled={Boolean(configError)}>
              Sign in
            </button>
          </form>
          <p className="admin-note">
            Tip: For production, avoid embedding long-lived AWS keys in the client. Use Cognito, STS tokens, or a backend to
            generate pre-signed URLs.
          </p>
        </section>
      </main>
    );
  }

  return (
    <main className="admin-page">
      <section className="admin-card">
        <div className="admin-card__header">
          <h1>Gallery Admin</h1>
          <button type="button" className="admin-secondary" onClick={handleLogout}>
            Sign out
          </button>
        </div>
        <p className="admin-helper">Upload event images and keep the gallery manifest in sync.</p>

        <form className="admin-form" onSubmit={handleUpload}>
          <label>
            Choose existing event
            <select value="" onChange={handleSelectExisting} disabled={!Object.keys(manifest).length}>
              <option value="">Select…</option>
              {Object.keys(manifest)
                .sort((a, b) => a.localeCompare(b))
                .map((key) => (
                  <option key={key} value={key}>
                    {key}
                  </option>
                ))}
            </select>
          </label>

          <label>
            Event name
            <input
              type="text"
              value={eventName}
              onChange={(e) => setEventName(e.target.value)}
              placeholder="e.g. Annual Day 2024"
            />
          </label>

          <label>
            Upload images
            <input type="file" accept="image/*" multiple onChange={handleFileChange} />
          </label>

          {selectedFiles.length > 0 && (
            <div className="admin-filelist">
              <strong>Selected ({selectedFiles.length}):</strong>
              <ul>
                {selectedFiles.map((file) => (
                  <li key={file.name}>{file.name}</li>
                ))}
              </ul>
            </div>
          )}

          <button type="submit" disabled={isBusy}>
            {isBusy ? 'Uploading…' : 'Upload & Update Manifest'}
          </button>
        </form>

        {statusMessage && <div className="admin-status">{statusMessage}</div>}
        {isLoadingManifest ? (
          <div className="admin-status">Loading manifest…</div>
        ) : (
          <div className="admin-manifest">
            <h2>Current Events</h2>
            {Object.keys(manifest).length === 0 ? (
              <p>No events found yet.</p>
            ) : (
              <ul>
                {Object.entries(manifest)
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([name, files]) => (
                    <li key={name}>
                      <strong>{name}</strong>
                      <span>{Array.isArray(files) ? `${files.length} images` : 'No images listed'}</span>
                    </li>
                  ))}
              </ul>
            )}
          </div>
        )}

        <div className="admin-note">
          <p>
            Remember to configure S3 bucket CORS to allow <code>GET</code>, <code>PUT</code>, and <code>POST</code> from your admin
            origin. If your bucket does not apply <code>public-read</code> by default, enable it via policy or set
            <code>REACT_APP_S3_SET_PUBLIC=true</code>.
          </p>
        </div>
      </section>
    </main>
  );
};

export default Admin;


